import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import type { Provider } from '@app/src/types/organizations'
import { useMemo } from 'react'

interface MyProfile {
  provider: Provider | null
  screeningAssessmentTemplateIds: number[]
  isAdverseScreeningInProgress: boolean
}

interface UseMyProfileResult {
  provider?: Provider
  screeningTemplateIds: Set<number>
  isAdverseScreeningInProgress: boolean
  hasInProgress: boolean
  isLoading: boolean
}

const POLL_INTERVAL_MS = 3000

const isScreeningInProgress = (profile: MyProfile | null | undefined): boolean =>
  Boolean(profile && (profile.screeningAssessmentTemplateIds.length > 0 || profile.isAdverseScreeningInProgress))

export function useMyProfile(pollWhileScreening: boolean): UseMyProfileResult {
  const { data, isLoading } = useFetchResource<MyProfile>({
    key: [FetchKey.Provider, 'my-profile'],
    endpoint: endpoints.myProfile,
    options: {
      refetchInterval: query =>
        pollWhileScreening || isScreeningInProgress(query.state.data) ? POLL_INTERVAL_MS : false,
    },
  })

  const screeningTemplateIds = useMemo(() => new Set(data?.screeningAssessmentTemplateIds ?? []), [data])
  const isAdverseScreeningInProgress = data?.isAdverseScreeningInProgress ?? false

  return {
    provider: data?.provider ?? undefined,
    screeningTemplateIds,
    isAdverseScreeningInProgress,
    hasInProgress: screeningTemplateIds.size > 0 || isAdverseScreeningInProgress,
    isLoading,
  }
}
